/**
 * AuthorListPage - Main author management page with list and search
 *
 * Displays authors with search functionality and pagination
 */
package com.castly.admin.ui.authors

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.AuthorDto
import com.castly.shared.dto.CreateAuthorRequest
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun AuthorListPage(
    apiClient: ApiClient,
    onAuthorClick: (Int) -> Unit = {}
) {
    val colors = AppTheme.colors
    val userRoleStr = window.localStorage.getItem("user_role") ?: "NONE"
    val canEdit = userRoleStr.uppercase() in setOf("LABEL_EDITOR", "EDITOR", "ADMIN", "SUPER_ADMIN")

    var authors by remember { mutableStateOf<List<AuthorDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var totalCount by remember { mutableStateOf(0) }
    var currentPage by remember { mutableStateOf(1) }
    var searchQuery by remember { mutableStateOf("") }
    var showCreateDialog by remember { mutableStateOf(false) }
    var isCreating by remember { mutableStateOf(false) }
    var createError by remember { mutableStateOf<String?>(null) }
    var expandedAuthorId by remember { mutableStateOf<Int?>(null) }
    var expandedAudiobooks by remember { mutableStateOf<List<com.castly.shared.dto.AudiobookDto>>(emptyList()) }
    var expandedLoading by remember { mutableStateOf(false) }

    val pageSize = 20
    val scope = rememberCoroutineScope()

    fun loadAuthors() {
        scope.launch {
            loading = true
            error = null
            try {
                val response = apiClient.listAuthors(
                    page = currentPage,
                    pageSize = pageSize,
                    query = searchQuery.takeIf { it.isNotBlank() }
                )
                authors = response.authors
                totalCount = response.total
            } catch (e: Exception) {
                error = e.message ?: "Failed to load authors"
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(currentPage, searchQuery) {
        loadAuthors()
    }

    Div({
        style {
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Color(colors.surfaceHover))
            themeTransition()
        }
    }) {
        // Header with search and create button
        Div({
            style {
                padding(Spacing.lg)
                backgroundColor(Color(colors.surface))
                property("border-bottom", "1px solid ${colors.border}")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(Spacing.lg)
                property("flex-shrink", "0")
                themeTransition()
            }
        }) {
            // Search bar
            Div({
                style {
                    flex("1")
                    maxWidth(400.px)
                }
            }) {
                Input(InputType.Text) {
                    value(searchQuery)
                    onInput { searchQuery = it.value; currentPage = 1 }
                    attr("placeholder", "Search authors...")
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.lg)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                    }
                }
            }

            // Create button
            if (canEdit) {
                com.castly.admin.ui.components.Tooltip(text = "Create Author") {
                    Button({
                        onClick { showCreateDialog = true }
                        attr("aria-label", "Create Author")
                        style {
                            padding(Spacing.sm)
                            backgroundColor(Color(colors.primary))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            width(36.px)
                            height(36.px)
                        }
                    }) {
                        com.castly.admin.ui.icons.IconPlus(size = 18)
                    }
                }
            }
        }

        // Main content area
        Div({
            style {
                flex("1")
                minHeight(0.px)
                overflow("auto")
                padding(Spacing.lg)
            }
        }) {
            // Loading state
            if (loading) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        padding(48.px)
                        color(Color(colors.textMuted))
                    }
                }) {
                    Text("Loading authors...")
                }
            }

            // Error state
            error?.let { errorMsg ->
                Div({
                    style {
                        backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                        border(1.px, LineStyle.Solid, Color(colors.danger))
                        borderRadius(BorderRadius.md)
                        padding(Spacing.lg)
                        marginBottom(Spacing.lg)
                        themeTransition()
                    }
                }) {
                    Div({
                        style {
                            fontSize(Typography.lg)
                            fontWeight("600")
                            color(Color(if (colors == LightColors) "#991b1b" else "#fca5a5"))
                            marginBottom(Spacing.sm)
                        }
                    }) {
                        Text("Error")
                    }
                    Text(errorMsg)
                }
            }

            // Empty state
            if (!loading && error == null && authors.isEmpty()) {
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        alignItems(AlignItems.Center)
                        justifyContent(JustifyContent.Center)
                        padding(48.px)
                        backgroundColor(Color(colors.surface))
                        borderRadius(BorderRadius.lg)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        themeTransition()
                    }
                }) {
                    H3({
                        style {
                            fontSize(Typography.xl)
                            fontWeight("600")
                            color(Color(colors.text))
                            marginBottom(Spacing.sm)
                            margin(0.px)
                        }
                    }) {
                        Text("No authors found")
                    }
                    P({
                        style {
                            fontSize(Typography.base)
                            color(Color(colors.textMuted))
                            marginBottom(Spacing.lg)
                        }
                    }) {
                        Text(if (searchQuery.isNotBlank()) "Try a different search term" else "Create your first author to get started")
                    }
                    if (canEdit && searchQuery.isBlank()) {
                        Button({
                            onClick { showCreateDialog = true }
                            style {
                                padding(10.px, Spacing.xl)
                                fontSize(Typography.base)
                                fontWeight("600")
                                color(Color(colors.surface))
                                backgroundColor(Color(colors.primary))
                                border(0.px)
                                borderRadius(BorderRadius.md)
                                cursor("pointer")
                            }
                        }) {
                            Text("+ Create First Author")
                        }
                    }
                }
            }

            // Authors list
            if (!loading && error == null && authors.isNotEmpty()) {
                Div({
                    style {
                        backgroundColor(Color(colors.surface))
                        borderRadius(BorderRadius.lg)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        overflow("hidden")
                        themeTransition()
                    }
                }) {
                    authors.forEachIndexed { index, author ->
                        val isExpanded = expandedAuthorId == author.id
                        Div({
                            style {
                                if (index > 0) property("border-top", "1px solid ${colors.border}")
                            }
                        }) {
                            // Author row (clickable to expand)
                            Div({
                                onClick {
                                    if (expandedAuthorId == author.id) {
                                        expandedAuthorId = null
                                    } else {
                                        expandedAuthorId = author.id
                                        expandedLoading = true
                                        scope.launch {
                                            try {
                                                expandedAudiobooks = apiClient.listAudiobooks(authorId = author.id).audiobooks
                                            } catch (_: Exception) {
                                                expandedAudiobooks = emptyList()
                                            } finally {
                                                expandedLoading = false
                                            }
                                        }
                                    }
                                }
                                style {
                                    padding(Spacing.lg, Spacing.xl)
                                    cursor("pointer")
                                    property("transition", "background-color 0.2s")
                                    if (isExpanded) backgroundColor(Color(colors.surfaceHover))
                                }
                                onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
                                onMouseOut { if (!isExpanded) it.target.asDynamic().style.backgroundColor = "transparent" }
                            }) {
                                Div({
                                    style {
                                        display(DisplayStyle.Flex)
                                        justifyContent(JustifyContent.SpaceBetween)
                                        alignItems(AlignItems.FlexStart)
                                        gap(Spacing.lg)
                                    }
                                }) {
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            alignItems(AlignItems.FlexStart)
                                            gap(Spacing.sm)
                                            flex("1")
                                            minWidth(0.px)
                                        }
                                    }) {
                                        Span({ style { fontSize(Typography.base); color(Color(colors.textMuted)); marginTop(4.px) } }) {
                                            Text(if (isExpanded) "▼" else "▶")
                                        }
                                        Div({
                                            style { flex("1"); minWidth(0.px) }
                                        }) {
                                            Div({
                                                style {
                                                    fontSize(Typography.lg)
                                                    fontWeight("600")
                                                    color(Color(colors.text))
                                                    marginBottom(Spacing.xs)
                                                    property("text-overflow", "ellipsis")
                                                    property("overflow", "hidden")
                                                    property("white-space", "nowrap")
                                                }
                                            }) {
                                                Text(author.name)
                                            }

                                            author.biography?.let { bio ->
                                                if (bio.isNotBlank()) {
                                                    Div({
                                                        style {
                                                            fontSize(Typography.base)
                                                            color(Color(colors.textMuted))
                                                            marginBottom(Spacing.sm)
                                                            property("display", "-webkit-box")
                                                            property("-webkit-line-clamp", "2")
                                                            property("-webkit-box-orient", "vertical")
                                                            property("overflow", "hidden")
                                                            property("text-overflow", "ellipsis")
                                                        }
                                                    }) {
                                                        Text(bio)
                                                    }
                                                }
                                            }

                                            Div({
                                                style {
                                                    fontSize(13.px)
                                                    color(Color(colors.textMuted))
                                                }
                                            }) {
                                                Text("${author.audiobookCount ?: 0} audiobook${if (author.audiobookCount != 1) "s" else ""}")
                                            }
                                        }
                                    }
                                }
                            }

                            // Expanded audiobooks panel
                            if (isExpanded) {
                                Div({
                                    style {
                                        padding(0.px, Spacing.lg, Spacing.lg, Spacing.lg)
                                        backgroundColor(Color(colors.surfaceHover))
                                        maxHeight(300.px)
                                        overflow("auto")
                                    }
                                }) {
                                    if (expandedLoading) {
                                        Div({ style { padding(Spacing.lg); color(Color(colors.textMuted)); textAlign("center") } }) {
                                            Text("Loading audiobooks...")
                                        }
                                    } else if (expandedAudiobooks.isEmpty()) {
                                        Div({ style { padding(Spacing.lg); color(Color(colors.textMuted)); textAlign("center") } }) {
                                            Text("No audiobooks found for this author")
                                        }
                                    } else {
                                        expandedAudiobooks.forEach { audiobook ->
                                            Div({
                                                onClick { it.stopPropagation() }
                                                style {
                                                    padding(Spacing.sm, Spacing.md)
                                                    backgroundColor(Color(colors.surface))
                                                    borderRadius(BorderRadius.sm)
                                                    marginBottom(Spacing.xs)
                                                    cursor("pointer")
                                                    display(DisplayStyle.Flex)
                                                    alignItems(AlignItems.Center)
                                                    gap(Spacing.sm)
                                                }
                                                onMouseOver { it.target.asDynamic().style.opacity = "0.8" }
                                                onMouseOut { it.target.asDynamic().style.opacity = "1" }
                                            }) {
                                                Span({ style { fontSize(Typography.xs); padding(2.px, 6.px); backgroundColor(Color("#4CAF50")); color(Color("#fff")); borderRadius(BorderRadius.sm) } }) { Text("Audiobook") }
                                                Span({ style { fontWeight("500"); color(Color(colors.text)) } }) { Text(audiobook.title) }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // Pagination
                if (totalCount > pageSize) {
                    val totalPages = (totalCount + pageSize - 1) / pageSize
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            justifyContent(JustifyContent.Center)
                            gap(Spacing.sm)
                            marginTop(Spacing.lg)
                            padding(Spacing.lg)
                        }
                    }) {
                        Button({
                            onClick { currentPage = maxOf(1, currentPage - 1) }
                            if (currentPage == 1) disabled()
                            style {
                                padding(Spacing.sm, Spacing.lg)
                                fontSize(Typography.base)
                                fontWeight("500")
                                color(if (currentPage == 1) Color(colors.textMuted) else Color(colors.text))
                                backgroundColor(Color(colors.surface))
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.md)
                                cursor(if (currentPage == 1) "not-allowed" else "pointer")
                            }
                        }) {
                            Text("← Previous")
                        }

                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                alignItems(AlignItems.Center)
                                padding(Spacing.sm, Spacing.lg)
                                fontSize(Typography.base)
                                color(Color(colors.text))
                            }
                        }) {
                            Text("Page $currentPage of $totalPages")
                        }

                        Button({
                            onClick { currentPage = minOf(totalPages, currentPage + 1) }
                            if (currentPage == totalPages) disabled()
                            style {
                                padding(Spacing.sm, Spacing.lg)
                                fontSize(Typography.base)
                                fontWeight("500")
                                color(if (currentPage == totalPages) Color(colors.textMuted) else Color(colors.text))
                                backgroundColor(Color(colors.surface))
                                border(1.px, LineStyle.Solid, Color(colors.border))
                                borderRadius(BorderRadius.md)
                                cursor(if (currentPage == totalPages) "not-allowed" else "pointer")
                            }
                        }) {
                            Text("Next →")
                        }
                    }
                }
            }
        }
    }

    // Create author dialog modal
    if (showCreateDialog) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                width(100.percent)
                height(100.percent)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                property("z-index", "1000")
            }
            onClick {
                if (!isCreating) {
                    showCreateDialog = false
                    createError = null
                }
            }
        }) {
            Div({
                style {
                    maxWidth(500.px)
                    width(90.percent)
                }
                onClick { it.stopPropagation() }
            }) {
                AuthorForm(
                    author = null,
                    isLoading = isCreating,
                    errorMessage = createError,
                    onSave = { name, biography ->
                        scope.launch {
                            isCreating = true
                            createError = null
                            try {
                                apiClient.createAuthor(CreateAuthorRequest(name, biography))
                                showCreateDialog = false
                                isCreating = false
                                loadAuthors()
                            } catch (e: Exception) {
                                createError = e.message ?: "Failed to create author"
                                isCreating = false
                            }
                        }
                    },
                    onCancel = {
                        if (!isCreating) {
                            showCreateDialog = false
                            createError = null
                        }
                    }
                )
            }
        }
    }
}

